/**
 * 全局现代 Toast：白底圆角卡片 + 状态图标 + 出入场动画。
 * 劫持 uni.showToast；H5 挂到 body，避免页面未包 layout / App 未渲染导致看不见。
 */
import { createApp, h } from 'vue';
import type { Pinia } from 'pinia';
import { useToastStore, type AppToastType } from '@/store/toast';
import AppToastHost from '@/components/app-toast/index.vue';

export interface ShowAppToastOptions {
  title?: string;
  icon?: 'success' | 'error' | 'fail' | 'exception' | 'loading' | 'none' | string;
  duration?: number;
  mask?: boolean;
  position?: string;
  image?: string;
}

let activePinia: Pinia | null = null;
let hostMounted = false;

const mapIconToType = (icon?: string): AppToastType => {
  const i = String(icon ?? 'success').toLowerCase();
  if (i === 'error' || i === 'fail' || i === 'exception') return 'error';
  if (i === 'none' || i === 'info') return 'info';
  return 'success';
};

const callNativeToast = (opts: ShowAppToastOptions) => {
  const native = (uni as any).__nativeShowToast as typeof uni.showToast | undefined;
  if (typeof native !== 'function') return;
  native.call(uni, {
    title: String(opts.title || ''),
    icon:
      opts.icon === 'error' || opts.icon === 'fail' || opts.icon === 'exception'
        ? 'error'
        : opts.icon === 'success'
          ? 'success'
          : 'none',
    duration: opts.duration || 2000,
  } as UniNamespace.ShowToastOptions);
};

function ensureToastHost() {
  if (hostMounted || typeof document === 'undefined') return;
  const exist = document.getElementById('app-toast-root');
  if (exist) {
    hostMounted = true;
    return;
  }
  if (!activePinia) return;

  const el = document.createElement('div');
  el.id = 'app-toast-root';
  document.body.appendChild(el);

  const hostApp = createApp({
    render: () => h(AppToastHost),
  });
  hostApp.use(activePinia);
  // 与主应用一致：勿把 component instance 打进 console
  hostApp.config.warnHandler = () => undefined;
  hostApp.mount(el);
  hostMounted = true;
}

export function showAppToast(options: ShowAppToastOptions | string): void {
  const opts: ShowAppToastOptions =
    typeof options === 'string' ? { title: options } : options || {};
  const title = String(opts.title || '').trim();
  if (!title) return;

  if (String(opts.icon || '').toLowerCase() === 'loading') {
    callNativeToast({ ...opts, title });
    return;
  }

  try {
    if (!activePinia) {
      callNativeToast(opts);
      return;
    }
    ensureToastHost();
    const store = useToastStore(activePinia);
    store.push({
      title,
      type: mapIconToType(opts.icon),
      duration: opts.duration,
    });
  } catch (e) {
    console.warn('[AppToast] push failed, fallback native', e);
    callNativeToast(opts);
  }
}

/** 在 createApp 且 app.use(pinia) 之后调用 */
export function installAppToast(pinia: Pinia): void {
  activePinia = pinia;
  if (typeof uni === 'undefined') return;

  // 尽早挂载宿主，避免首条 toast 时序问题
  if (typeof document !== 'undefined') {
    const mount = () => ensureToastHost();
    if (document.body) mount();
    else document.addEventListener('DOMContentLoaded', mount, { once: true });
  }

  if ((uni as any).__appToastPatched) return;

  const nativeShow = uni.showToast.bind(uni);
  (uni as any).__nativeShowToast = nativeShow;
  (uni as any).__appToastPatched = true;

  uni.showToast = ((options: UniNamespace.ShowToastOptions) => {
    showAppToast(options || {});
  }) as typeof uni.showToast;

  const nativeHide = uni.hideToast?.bind(uni);
  uni.hideToast = (() => {
    try {
      if (activePinia) useToastStore(activePinia).clear();
    } catch {
      // ignore
    }
    nativeHide?.();
  }) as typeof uni.hideToast;
}
